import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { Template as ReviewsSPA } from "./ReviewsPageTemplate";
import { Template as WriteReviewButton } from "./WriteReviewButtonTemplate";

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the in-tree
// SiteHeader chain, which is typed with mutable reactivated props.
export const Template = (
    props: templates.ReviewsPageDocumentTemplate,
): React.JSX.Element => {
    const {
        show_sources,
        hero_image_url,
        hero_content,
        footer_content,
        product_type_whitelist_ids,
        iovation_url,
    } = props;
    const chrome = React.useContext(Context).chrome;

    // Render one hero/footer content block. ``title`` is plain (autoescaped) JSX
    // text; ``subtitle`` / ``text`` are server-expanded rich-text HTML injected
    // via dangerouslySetInnerHTML (CMS-authored, same trust model as before).
    const renderContentBlock = (
        block: (typeof hero_content)[number],
        index: number,
    ): React.JSX.Element => {
        switch (block.block_type) {
            case "title":
                return (
                    <h2 key={index} className="customer-review__header-title">
                        {block.content}
                    </h2>
                );
            case "subtitle":
                return (
                    <div
                        key={index}
                        className="customer-review__header-subtitle"
                        dangerouslySetInnerHTML={{ __html: block.content }}
                    />
                );
            case "text":
                return (
                    <div
                        key={index}
                        className="reviews-standalone-footer__block"
                        dangerouslySetInnerHTML={{ __html: block.content }}
                    />
                );
            default: {
                const _exhaustive: never = block.block_type;
                return _exhaustive;
            }
        }
    };

    const headerClass = [
        "customer-review__header",
        show_sources ? "" : "customer-review__header--standalone",
        hero_content.length > 0 ? "" : "customer-review__header--empty",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            // Standalone widget (show_sources=false) swaps the site chrome for a
            // bare <header> and a standalone footer. They render in the
            // header/footer slots so they stay siblings of <main> under .layout,
            // which the puppeteer chrome selectors depend on.
            header={show_sources ? undefined : <header></header>}
            footer={
                show_sources ? undefined : (
                    <div className="reviews-standalone-footer">
                        <div className="reviews-standalone-footer__container l-capped-width">
                            {footer_content.map(renderContentBlock)}
                        </div>
                    </div>
                )
            }
        >
            <div
                className={`hero hero--reviews ${
                    hero_image_url ? "" : "hero--reviews-empty"
                }`.trimEnd()}
                style={{ backgroundImage: `url('${hero_image_url}')` }}
            ></div>
            <div className="l-capped-width reviews-page-container">
                <div className="reviews-app-container">
                    <div className="customer-review l-capped-width">
                        <div className={headerClass}>
                            <div className="customer-review__header-copy">
                                {hero_content.map(renderContentBlock)}
                            </div>
                            {show_sources && (
                                <WriteReviewButton
                                    brand=""
                                    product_type_whitelist={
                                        product_type_whitelist_ids
                                    }
                                />
                            )}
                        </div>
                        <ReviewsSPA {...props} />
                    </div>
                </div>
            </div>
            <IovationScripts
                iovationUrl={iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
